import { useEffect, useRef, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import InputAdornment from '@zenbusiness/zui/InputAdornment';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import PropTypes from 'prop-types';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { DATE_FORMAT } from '#client/constants/date';

import {
  DesktopDatePickerStyled,
  MobileDatePickerStyled
} from './components/DesktopDatePicker.styles';

dayjs.extend(isBetween);

const MIN_AGE = 18;

const earliestAllowedDate = dayjs('1901-01-01');
const latestAllowedDate = dayjs().subtract(MIN_AGE, 'year');

const ERROR_MESSAGE_OVER_AGE = 'Please enter a valid DOB';
const ERROR_MESSAGE_UNDER_AGE = 'Applicants must be at least 18 years old';
const ERROR_MESSAGE_INVALID_DATE = 'Please enter a valid date of birth (mm-dd-yyyy)';

const CalendarIcon = () => (
  <SharedAsset
    alt=""
    src="/icon/micro/calendar-date-picker.svg"
  />
);

export const BirthdayUIField = ({
  field = { setSaved: () => {} },
  value = '',
  dataChangeHandler = () => {},
  disabled
}) => {
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const [values, setValues] = useState({
    birthday: value ? dayjs(value, DATE_FORMAT.BIRTHDAY) : null,
    valid: Boolean(value),
    message: value ? '' : ERROR_MESSAGE_INVALID_DATE,
    isFocus: false,
    isDirty: false
  });

  const desktopCalendarRef = useRef();

  const [isMobileDatePickerOpen, setMobileDatePickerOpen] = useState(false);

  const onMobileDatePickerClose = () => {
    setMobileDatePickerOpen(false);
  };

  const onMobileDatePickerOpen = () => {
    setMobileDatePickerOpen(true);
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const isShowError = values.isDirty && !values.isFocus && !values.valid;

  /**
   * Validate the date of birth
   * @param {object} selectedDate  - selected date from the date picker
   * @returns {object} - object with valid flag and message
   */
  const validateDateOfBirth = (selectedDate) => {
    if (!selectedDate?.isValid()) {
      return {
        valid: false,
        message: ERROR_MESSAGE_INVALID_DATE
      };
    }
    // The date picker in banking application should not go beyond 1901,
    // if a customer manually enters a date beyond that, they should be given an error
    if (selectedDate.isBefore(earliestAllowedDate)) {
      return { valid: false, message: ERROR_MESSAGE_OVER_AGE };
    }

    // The date picker in banking application should not allow customers under 18 years old.
    // If a customer manually enters a date that would make them under 18, they should be given an error
    if (selectedDate.isAfter(latestAllowedDate)) {
      return { valid: false, message: ERROR_MESSAGE_UNDER_AGE };
    }

    return {
      valid: true,
      message: ''
    };
  };

  const trackBirthdayChangeClick = useButtonPressedCallback({
    elementText: 'date_of_birth',
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  /**
   * Handle the change event of the date picker
   * @param {object} selectedDate - selected date from the date picker
   * @returns {void}
   */
  const handleChange = (selectedDate) => {
    const { valid, message } = validateDateOfBirth(selectedDate);

    setValues({
      ...values,
      birthday: selectedDate,
      valid,
      message
    });

    const formattedDateOfBirth = selectedDate?.format(DATE_FORMAT.BIRTHDAY);
    if (valid) {
      field.setLoading();
      /**
       * Tracking Details: Tracking to be added to the banking application Collect Info screen.
       * Question:
       *          Where are customers typically dropping off on this screen?
       *          Do they typically drop off at SSN?
       *          How often are they viewing the user agreements?
       */
      trackBirthdayChangeClick();
    }

    dataChangeHandler(
      valid,
      { bankAccountMembers: [{ dateOfBirth: formattedDateOfBirth }] },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError, valid);
        forceUpdate();
      }
    );
  };

  const handleFocus = (event) => {
    setAnchorEl(event.currentTarget);
    setValues({ ...values, isFocus: true });
  };

  const handleBlur = () => {
    setValues({ ...values, isFocus: false, isDirty: true });
  };

  useEffect(() => {
    setAnchorEl(desktopCalendarRef.current);
  }, []);

  return (
    <Stack gap="8px">
      <FormControl
        fullWidth
        variant="outlined"
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {isMobile ? (
            <MobileDatePickerStyled
              disabled={disabled}
              disableFuture
              inputFormat={DATE_FORMAT.BIRTHDAY}
              label="Date of Birth"
              minDate={earliestAllowedDate}
              onChange={handleChange}
              onClose={onMobileDatePickerClose}
              onOpen={onMobileDatePickerOpen}
              open={isMobileDatePickerOpen}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={isShowError}
                  inputProps={{
                    ...params.inputProps,
                    inputMode: 'numeric'
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: !isMobileDatePickerOpen && (
                      <InputAdornment
                        onClick={onMobileDatePickerOpen}
                        position="end"
                      >
                        <CalendarIcon />
                      </InputAdornment>
                    )
                  }}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                />
              )}
              value={values.birthday}
            />
          ) : (
            <DesktopDatePickerStyled
              components={{
                OpenPickerIcon: CalendarIcon
              }}
              disabled={disabled}
              disableFuture
              inputFormat={DATE_FORMAT.BIRTHDAY}
              label="Date of Birth"
              minDate={earliestAllowedDate}
              onChange={handleChange}
              PopperProps={{
                placement: 'bottom-end',
                anchorEl
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={isShowError}
                  inputProps={params.inputProps}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    ...params.InputProps
                  }}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  ref={desktopCalendarRef}
                />
              )}
              value={values.birthday}
            />
          )}
        </LocalizationProvider>
      </FormControl>
      {isShowError && (
        <Text
          color="red"
          component="div"
          size="xs"
        >
          {values.message}
        </Text>
      )}
    </Stack>
  );
};

BirthdayUIField.propTypes = {
  value: PropTypes.string,
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({ setSaved: PropTypes.func, setLoading: PropTypes.func }),
  disabled: PropTypes.bool
};
